// eslint-disable-next-line no-unused-vars
import React, { useEffect, useState, useMemo } from 'react'
import { Flex, Pane, Text } from '@leverege/ui-elements'
import { Constants } from '@leverege/api-redux'
import { groupPlyLayers, canPlyLayerOverride } from './Util'
import PlyLayerGroup from './components/PlyLayerGroup'
import useData from './hooks/useData'
import PlyLayerNoData from './PlyLayerNoData'

import S from './PlyLayerShopFloor.css'

const DATE_FORMAT = {
  month : 'numeric',
  day : 'numeric',
  hour : 'numeric',
  minute : 'numeric',
  second : 'numeric',
}

export default function PlyLayerShopFloor( props ) {
  const { delegate, parentItem, profile } = props
  const shellName = parentItem?.data?.name
  const locationId = parentItem?.data?.data?.location?.id || null
  const shellId = parentItem?.id || null
  const subAssemblyJobId = parentItem?.data?.data?.subAssemblyJob?.id || null
  const nextSubAssemblyJobId = parentItem?.data?.data?.nextSubAssemblyJob?.id || null
  const [ permissions, setPermissions ] = useState( {
    canToleranceOverride : false,
    canOcrOverride : false
  } )
  const userInfo = useMemo( () => {
    return {
      userEmail : profile?.email || null,
      userId : profile?.id || null
    }
  }, [ profile ] )

  useEffect( () => {
    const checkPermissions = async () => {
      try {
        const [ tolerancePermission, ocrPermission ] = await Promise.all( [
          canPlyLayerOverride( {
            locationId,
            type : 'Tolerance',
          } ),
          canPlyLayerOverride( {
            locationId,
            type : 'Ocr',
          } )
        ] )
        
        setPermissions( {
          canToleranceOverride : tolerancePermission,
          canOcrOverride : ocrPermission
        } )
      } catch ( err ) {
        console.error( 'Error checking permissions:', err )
        setPermissions( {
          canToleranceOverride : false,
          canOcrOverride : false
        } )
      }
    }
  
    checkPermissions()
  }, [ locationId ] )

  const { plyData, lastUpdated, plyLayerDelegate, isNextSA } = useData( { delegate } )

  const effectiveSubAssemblyJobId = useMemo( () => {
    return isNextSA ? nextSubAssemblyJobId : subAssemblyJobId
  }, [ isNextSA, nextSubAssemblyJobId, subAssemblyJobId ] )

  const { currentLaserLayer } = useMemo( () => {
    return groupPlyLayers( plyData )
  }, [ plyData ] )

  const laserLayerTitle = useMemo( () => {
    if ( !currentLaserLayer?.laserLayer ) return 'Current Laser Projection'

    return currentLaserLayer.laserLayer
  }, [ currentLaserLayer ] )

  if ( currentLaserLayer.groupedLayers.length === 0 || plyData.status === Constants.INTERFACE_STATUS_ERROR ) {
    return (
      <PlyLayerNoData shellName={shellName} isNextSA={isNextSA}/>
    )
  } 

  return (
    <Flex variant='col' className={S.container}>
      <Pane>
        <Pane variant='mobileCardCustomHeader'>
          <Flex variant='colM'>
            <Text variant='mobilePaceCardBigHeader'>
              {shellName}
              {isNextSA && <Text as="span" variant='mobileSmallLabel'>Next SA</Text>}
            </Text>
            <Text variant='paceCardDetailHeader'>
              Last Updated: { lastUpdated.toLocaleString( DATE_FORMAT ) }
            </Text>
          </Flex>
        </Pane>
      </Pane>
      <Text variant='mobilePaceCardHeader' className={S.laserLayerTitle}>
        {laserLayerTitle}
      </Text>
      <Flex variant='col' className={S.layerGroupsContainer}>
        {currentLaserLayer.groupedLayers.map( ( [ name, layers ] ) => (
          <PlyLayerGroup 
            key={name}
            name={name}
            data={layers}
            maxPlyLayerCount={Math.max( layers.length, 2 )}
            plyDelegate={plyLayerDelegate}
            locationId={locationId}
            shellId={shellId}
            subAssemblyJobId={effectiveSubAssemblyJobId}
            userInfo={userInfo}
            permissions={permissions}
            isNextSA={isNextSA}/>
        ) )}
      </Flex>
    </Flex>
  )
}
